import { useRef } from 'react';
import type { ItineraryDay } from '../../../../mocks/itinerary';
import { serviceTypeConfig } from '../../../../mocks/itinerary';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

interface Props {
  title: string;
  destination: string;
  startDate: string;
  endDate: string;
  nights: number;
  pax: number;
  currency: string;
  days: ItineraryDay[];
  onClose: () => void;
}

export default function PDFPreview({ title, destination, startDate, endDate, nights, pax, currency, days, onClose }: Props) {
  const printRef = useRef<HTMLDivElement>(null);

  const allServices = days.flatMap((d) => d.services);
  const totalCost = allServices.reduce((sum, s) => sum + s.costPrice, 0);
  const totalSell = allServices.reduce((sum, s) => sum + s.sellPrice, 0);
  const profit = totalSell - totalCost;
  const margin = totalSell > 0 ? Math.round(((profit) / totalSell) * 100) : 0;
  const perPerson = pax > 0 ? Math.round(totalSell / pax) : totalSell;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body > *:not(#pdf-root) { display: none !important; }
          #pdf-root { position: fixed; inset: 0; z-index: 9999; background: white; }
          .pdf-no-print { display: none !important; }
          .pdf-content { box-shadow: none !important; }
          @page { margin: 15mm; size: A4; }
        }
      `}</style>

      <div id="pdf-root" className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8 px-4">
        {/* Toolbar */}
        <div className="pdf-no-print fixed top-4 right-4 flex items-center gap-2 z-50">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-printer-line" />
            Print / Save as PDF
          </button>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-white text-gray-500 hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* PDF Content */}
        <div
          id="pdf-content"
          ref={printRef}
          className="pdf-content bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden"
        >
          {/* Cover Header */}
          <div className="relative h-48 overflow-hidden" style={{ backgroundColor: brand }}>
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${brand}CC, ${brand}99)` }} />
            <div className="absolute inset-0 flex flex-col justify-between p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                    {agencyInfo.logo}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{agencyInfo.name}</p>
                    <p className="text-white/60 text-xs">Travel Itinerary</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white/70 text-xs">Generated</p>
                  <p className="text-white text-sm font-medium">{formatDate(new Date().toISOString().split('T')[0])}</p>
                </div>
              </div>
              <div>
                <h1 className="text-white text-2xl font-bold mb-1">{title || 'Travel Itinerary'}</h1>
                <div className="flex items-center gap-4 text-white/80 text-sm">
                  <span className="flex items-center gap-1"><i className="ri-map-pin-line" />{destination}</span>
                  <span className="flex items-center gap-1"><i className="ri-calendar-line" />{formatDate(startDate)} — {formatDate(endDate)}</span>
                  <span className="flex items-center gap-1"><i className="ri-moon-line" />{nights} Nights</span>
                  <span className="flex items-center gap-1"><i className="ri-user-line" />{pax} Pax</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Summary Bar */}
          {totalSell > 0 && (
            <div className="grid grid-cols-4 divide-x divide-gray-100 bg-gray-50 border-b border-gray-100">
              {[
                { label: 'Total Cost', value: `${currency} ${totalCost.toLocaleString()}`, color: '#6B7280' },
                { label: 'Total Sell', value: `${currency} ${totalSell.toLocaleString()}`, color: '#1F2937' },
                { label: 'Profit', value: `${currency} ${profit.toLocaleString()}`, color: '#10B981' },
                { label: 'Per Person', value: `${currency} ${perPerson.toLocaleString()}`, color: brand },
              ].map((item) => (
                <div key={item.label} className="p-4 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                  <p className="text-sm font-bold" style={{ color: item.color }}>{item.value}</p>
                  {item.label === 'Profit' && <p className="text-xs text-gray-400">{margin}% margin</p>}
                </div>
              ))}
            </div>
          )}

          {/* Day by Day */}
          <div className="p-8 space-y-8">
            {days.map((day) => (
              <div key={day.id}>
                {/* Day Header */}
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0"
                    style={{ backgroundColor: brand }}
                  >
                    <span className="text-xs leading-none">Day</span>
                    <span className="text-lg font-black leading-none">{day.dayNumber}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-800">{day.location}</h3>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                      <span>{formatDate(day.date)}</span>
                      {day.accommodation && (
                        <span className="flex items-center gap-1">
                          <i className="ri-hotel-line" />
                          {day.accommodation}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="space-y-2 ml-16">
                  {day.services.map((service) => {
                    const cfg = serviceTypeConfig[service.type];
                    return (
                      <div key={service.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: cfg.bg, color: cfg.color }}
                        >
                          <i className={`${cfg.icon} text-sm`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-gray-800">{service.name}</p>
                              {service.time && (
                                <span className="text-xs text-gray-400">{service.time}</span>
                              )}
                            </div>
                            {service.sellPrice > 0 && (
                              <span className="text-xs font-semibold text-gray-600 flex-shrink-0">
                                {currency} {service.sellPrice.toLocaleString()}
                              </span>
                            )}
                          </div>
                          {service.description && (
                            <p className="text-xs text-gray-500 mt-1">{service.description}</p>
                          )}
                          {service.notes && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <i className="ri-information-line" />
                              {service.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {day.services.length === 0 && (
                    <p className="text-sm text-gray-300 italic py-2">No services planned for this day.</p>
                  )}
                </div>

                {day.dayNumber < days.length && (
                  <div className="flex items-center gap-4 mt-6">
                    <div className="flex-1 h-px bg-gray-100" />
                    <i className="ri-arrow-down-line text-gray-200" />
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-8 py-5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
            <span>{agencyInfo.name} — Travel Itinerary</span>
            <span>Generated by Travel Booking AI Assistance</span>
          </div>
        </div>
      </div>
    </>
  );
}
